import { z } from 'zod';
/**
 * Two roles, stored in Postgres and read live on every request.
 *
 * These used to be Entra *app roles* (`MCI.Consultant`/`MCI.Admin`) read off a
 * token claim. They are not any more: since auth moved to server-brokered OAuth
 * and session cookies, role comes only from `users.role`, so a demotion takes
 * effect on the next request rather than whenever a token expires. The Entra
 * app registration no longer supplies role at all — which is what made renaming
 * these values safe. See security.md §3, §4.
 */
export const AppRole = z.enum(['Admin', 'User']);
export const ADMIN = 'Admin';
export const USER = 'User';
/**
 * Whether an account may actually use the application, independent of its role.
 *
 * A first sign-in creates a `pending` account: authenticating proves who you
 * are, it does not grant access. Only an Admin moves an account to `active`.
 * Every API route except the session/logout pair refuses a non-`active`
 * account — see apps/api/src/plugins/auth.ts.
 *
 * `rejected` and `disabled` are deliberately distinct: the first is "we looked
 * at this request and declined it", the second is "this was a real user and is
 * not any more". Both refuse access identically; keeping them apart is so the
 * admin screen and the activity log can tell the difference a year later.
 */
export const AccountStatus = z.enum(['pending', 'active', 'rejected', 'disabled']);
/** The authenticated caller, built from a verified session. */
export const AuthenticatedUser = z
    .object({
    /**
     * Entra object id — the identity key. Never `upn`, which is mutable.
     * Not `.uuid()`: real sign-ins still produce one, but dev-login's
     * synthetic identity (see apps/api/src/routes/auth.ts) is not a UUID,
     * and this is now sourced from a database row rather than validated
     * directly off a token claim, so the stricter format buys nothing.
     *
     * Note this is *identity*, not ownership: scenarios key on the numeric
     * `users.id` (`request.session.id`), not on this. See
     * packages/shared/src/scenario.ts.
     */
    oid: z.string().min(1),
    tenantId: z.string().uuid(),
    upn: z.string(),
    displayName: z.string().nullable(),
    roles: z.array(AppRole).min(1),
    /**
     * Carried on the caller so a route can reason about it without a second
     * database round trip. A non-`active` caller never reaches a route that
     * does real work — the auth plugin stops it first — but /auth/session
     * must still answer for one, so the client can render the
     * awaiting-approval screen instead of a broken app.
     */
    status: AccountStatus,
})
    .strict();
export function hasRole(user, role) {
    return user.roles.includes(role);
}
export function isAdmin(user) {
    return hasRole(user, ADMIN);
}
//# sourceMappingURL=roles.js.map